import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Settings,
  Zap,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Plus,
  Pencil,
  Trash2,
  Activity,
  GitBranch,
  X,
} from 'lucide-react';
import KarpenterPoolFormModal from './KarpenterPoolFormModal';
import KarpenterNodeClassFormModal from './KarpenterNodeClassFormModal';
import GitOpsSnippetsModal from './GitOpsSnippetsModal';

/**
 * Karpenter Optimization Panel (Phase A + B, AWS + GCP only).
 *
 * Mirrors the ScaleOps Node Pools page: per-pool savings, recommended instance
 * families, and a one-click "Optimize Now" button that routes through the
 * existing operator-approval flow (creates a pending karpenter_optimize_pool
 * action — operator approves via the recommendations table; agent applies).
 *
 * Hidden when the cluster has no Karpenter inventory (CA-only clusters or
 * Azure clusters where the backend short-circuits ingest).
 *
 * Data sources:
 *   GET /v1/clusters/{id}/karpenter/nodepools     — pool list + per-pool savings
 *   GET /v1/clusters/{id}/karpenter/nodeclasses   — EC2NodeClass / GCPNodeClass
 *   POST /v1/clusters/{id}/karpenter/nodepools/{name}/optimize-instance-type
 *      → emits a pending action operator approves separately
 */

const POOL_REFRESH_MS = 60_000;
const NODECLASS_REFRESH_MS = 60_000;

// ─── Filter chips ────────────────────────────────────────────────────────────

const FILTERS = {
  all: { label: 'All', match: () => true },
  // E.2 — pools blocking pending pods. Highest-priority filter so
  // operators can find scheduling problems first.
  schedulability: {
    label: 'Pods Blocked',
    match: (p) => !!p.schedulability_recommendation,
  },
  savings: {
    label: 'Savings Available',
    match: (p) => (p.savings_instance_type_monthly || 0) > 0,
  },
  // E.0 — pools whose family/size constraints exclude cheaper instance types.
  widening: {
    label: 'Widening Available',
    match: (p) => !!p.recommended_widening,
  },
  pending: {
    label: 'Optimization Pending',
    match: (p) => p.status === 'pending_approval',
  },
};

// ─── Money formatting ────────────────────────────────────────────────────────

const fmtMo = (v) => {
  const n = Number(v || 0);
  if (n > 0 && n < 1) return '<$1/mo';
  return `$${Math.round(n).toLocaleString()}/mo`;
};

// ─── Status pill ─────────────────────────────────────────────────────────────

const StatusPill = ({ status, savings, wideningSavings, blockedPodCount }) => {
  if (status === 'pending_approval') {
    return (
      <Badge className="bg-amber-100 text-amber-800 inline-flex items-center gap-1">
        <Clock className="h-3 w-3" /> Pending approval
      </Badge>
    );
  }
  if (status === 'schedulability_blocked') {
    // E.2 — red, urgency cue. Pods can't run until pool widens.
    return (
      <Badge
        className="bg-red-100 text-red-800 inline-flex items-center gap-1"
        title="Pending pods cannot schedule on this pool — widen to unblock"
      >
        <AlertTriangle className="h-3 w-3" />
        {blockedPodCount > 0
          ? `${blockedPodCount} pod${blockedPodCount > 1 ? 's' : ''} blocked`
          : 'Pods blocked'}
      </Badge>
    );
  }
  if (status === 'savings_available') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
        <Zap className="h-3 w-3" /> {fmtMo(savings)}
      </Badge>
    );
  }
  if (status === 'widening_available') {
    // E.0 — pool's family/size constraints exclude cheaper types.
    return (
      <Badge
        className="bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
        title="Allowing more instance types would unlock cheaper capacity"
      >
        <Zap className="h-3 w-3" /> +{fmtMo(wideningSavings)} available
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-700 inline-flex items-center gap-1">
      <CheckCircle2 className="h-3 w-3" /> Up to date
    </Badge>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const lifecycleBadge = (lifecycle) => (
  <Badge className="bg-slate-100 text-slate-700">
    {lifecycle || 'on-demand'}
  </Badge>
);

const summarizeFamilies = (pool) => {
  const families = pool.instance_families || [];
  if (families.length === 0) {
    return <span className="text-slate-400 text-xs">any</span>;
  }
  if (families.length <= 3) {
    return <span className="font-mono text-xs">{families.join(', ')}</span>;
  }
  return (
    <span className="font-mono text-xs" title={families.join(', ')}>
      {`${families.slice(0, 3).join(', ')} +${families.length - 3}`}
    </span>
  );
};

const summarizeDisruption = (pool) => {
  const d = pool.disruption || {};
  const policy = d.consolidation_policy || '—';
  const after = d.consolidate_after || '';
  return (
    <span className="font-mono text-xs">
      {after ? `${policy} / ${after}` : policy}
    </span>
  );
};

// ─── NodeClass sub-table ─────────────────────────────────────────────────────

const NodeClassTable = ({ nodeclasses, cloud, onCreate, onEdit, onDelete }) => {
  // C.5: render the table even when empty so the operator can still
  // click "Create" — previously this returned null on empty arrays.
  const list = nodeclasses || [];

  const columns =
    cloud === 'aws'
      ? [
          { key: 'name', label: 'Name' },
          { key: 'ami_family', label: 'AMI Family' },
          { key: 'role', label: 'IAM Role' },
          { key: 'instance_profile', label: 'Instance Profile' },
        ]
      : [
          { key: 'name', label: 'Name' },
          { key: 'image_family', label: 'Image Family' },
          { key: 'gcp_project', label: 'Project' },
          { key: 'region', label: 'Region' },
          { key: 'service_account', label: 'Service Account' },
        ];

  return (
    <Card className="mt-3">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings className="h-4 w-4 text-slate-400" />
          {cloud === 'aws' ? 'EC2NodeClasses' : 'GCPNodeClasses'}
          <Badge className="bg-slate-100 text-slate-600 text-xs">
            {list.length}
          </Badge>
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          disabled={!cloud || !onCreate}
          onClick={onCreate}
          className="inline-flex items-center gap-1"
        >
          <Plus className="h-3 w-3" /> Create NodeClass
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left border-b">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">
                    {c.label}
                  </th>
                ))}
                <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium" />
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="py-3 text-center text-slate-400">
                    No node classes yet.
                  </td>
                </tr>
              )}
              {list.map((nc) => (
                <tr key={nc.name} className="border-b last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className="py-1.5 pr-3">
                      {nc[c.key] || (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  ))}
                  <td className="py-1.5 pr-3">
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onEdit?.(nc)}
                        disabled={!onEdit}
                        title="Edit NodeClass"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onDelete?.(nc)}
                        disabled={!onDelete}
                        title="Delete NodeClass"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main panel ──────────────────────────────────────────────────────────────

const KarpenterPanel = ({ clusterId }) => {
  const [pools, setPools] = useState(null);
  const [nodeclasses, setNodeclasses] = useState(null);
  const [cloud, setCloud] = useState(null);
  const [staleness, setStaleness] = useState(null);
  const [filter, setFilter] = useState('all');
  const [optimizing, setOptimizing] = useState({}); // poolName → bool
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  // Phase C: form modal state. `mode` ∈ 'create' | 'edit' | 'delete' | null.
  const [modal, setModal] = useState({ open: false, mode: null, pool: null });
  // Holds the latest disruption recommendation for the preview dialog.
  // Shape: { pool, recommendation, confidence, reasoning, current, signals, history_count }
  const [disruptionPreview, setDisruptionPreview] = useState(null);
  // Phase C.5: NodeClass form modal — same shape but the pool field
  // holds the NodeClass row instead.
  const [ncModal, setNcModal] = useState({ open: false, mode: null, nodeclass: null });
  // W7: GitOps steering-snippets modal (Argo CD / Flux ignore config).
  const [gitopsOpen, setGitopsOpen] = useState(false);

  // Pool fetch
  useEffect(() => {
    if (!clusterId) return;
    let cancelled = false;
    const fetchPools = async () => {
      try {
        const res = await axios.get(
          `${API}/v1/clusters/${clusterId}/karpenter/nodepools`,
        );
        if (cancelled) return;
        setPools(res.data?.pools || []);
        setCloud(res.data?.cloud_provider || null);
        setStaleness(res.data?.staleness_seconds);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message);
        }
      }
    };
    fetchPools();
    const id = setInterval(fetchPools, POOL_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clusterId]);

  // NodeClass fetch
  useEffect(() => {
    if (!clusterId) return;
    let cancelled = false;
    const fetchClasses = async () => {
      try {
        const res = await axios.get(
          `${API}/v1/clusters/${clusterId}/karpenter/nodeclasses`,
        );
        if (!cancelled) setNodeclasses(res.data?.nodeclasses || []);
      } catch {
        // best-effort; nodeclass table just hides on failure
      }
    };
    fetchClasses();
    const id = setInterval(fetchClasses, NODECLASS_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clusterId]);

  const visiblePools = useMemo(() => {
    if (!pools) return null;
    return pools.filter(FILTERS[filter].match);
  }, [pools, filter]);

  const totalSavings = useMemo(() => {
    if (!pools) return 0;
    return pools.reduce(
      (acc, p) => acc + Number(p.savings_instance_type_monthly || 0),
      0,
    );
  }, [pools]);

  // Hide entirely when cluster has no Karpenter inventory at all.
  // The empty + cloud=null case happens for CA-only clusters and for Azure
  // (the backend short-circuits ingest there).
  if (pools && pools.length === 0 && cloud === null) {
    return null;
  }

  const handleOptimize = async (pool) => {
    if (!clusterId) return;
    setOptimizing((s) => ({ ...s, [pool.name]: true }));
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/optimize-instance-type`,
      );
      // Optimistically flip the row's status — next 60s poll will reconcile.
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'pending_approval', pending_action_id: res.data?.action_id }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [pool.name]: false }));
    }
  };

  // Approve / dismiss a pending pool action inline. The old backend-recommender
  // "Node Recommendations" table (where approvals used to live) is retired under
  // system-of-record mode, so pool actions are approved right here on the row.
  const handleApprove = async (pool) => {
    if (!clusterId || !pool.pending_action_id) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__approve`]: true }));
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/node-optimization/recommendations/${pool.pending_action_id}/approve`,
      );
      // Clear the pending flag optimistically; the 60s poll reconciles the
      // real applied state (up_to_date / savings_available).
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'approving', pending_action_id: null }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__approve`]: false }));
    }
  };

  const handleDismiss = async (pool) => {
    if (!clusterId || !pool.pending_action_id) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__dismiss`]: true }));
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/node-optimization/recommendations/${pool.pending_action_id}/reject`,
      );
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'up_to_date', pending_action_id: null }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__dismiss`]: false }));
    }
  };

  // Phase D.1: server-computed disruption-policy recommendation. Same
  // optimistic flip as handleOptimize. Backend returns 400 when there's
  // not enough churn history; surface that as a non-fatal info banner.
  // Two-step disruption optimize. handleOptimizeDisruption (re-named
  // openDisruptionPreview) only fetches the recommendation + opens the
  // preview dialog. confirmOptimizeDisruption is what actually emits the
  // action after the operator clicks Apply with the reasoning visible.
  const openDisruptionPreview = async (pool) => {
    if (!clusterId) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__disrupt`]: true }));
    try {
      const res = await axios.get(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/disruption-recommendation`,
      );
      setDisruptionPreview({ pool, ...res.data });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__disrupt`]: false }));
    }
  };

  const confirmOptimizeDisruption = async () => {
    if (!disruptionPreview?.pool) return;
    const pool = disruptionPreview.pool;
    setOptimizing((s) => ({ ...s, [`${pool.name}__disrupt`]: true }));
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/optimize-disruption-policy`,
      );
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'pending_approval', pending_action_id: res.data?.action_id }
            : p,
        ),
      );
      setDisruptionPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__disrupt`]: false }));
    }
  };

  // Old name kept as a thin alias so the existing JSX onClick wires don't break.
  const handleOptimizeDisruption = openDisruptionPreview;

  // E.0: resolve a "Widening Available" status by emitting a pending
  // karpenter_update_pool action with the minimal patch the recommender
  // produced. Same optimistic flip + approval rail as the other actions.
  const handleResolveWidening = async (pool) => {
    if (!clusterId) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__widen`]: true }));
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/optimize-widening`,
      );
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'pending_approval', pending_action_id: res.data?.action_id }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__widen`]: false }));
    }
  };

  // Sprint 8 #1: emit a karpenter_update_pool action whose patch drops
  // instance sizes flagged as fragmentation traps. Symmetric to widening
  // — same dispatcher, opposite direction.
  const handleResolveNarrowing = async (pool) => {
    if (!clusterId) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__narrow`]: true }));
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/optimize-narrowing`,
      );
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'pending_approval', pending_action_id: res.data?.action_id }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__narrow`]: false }));
    }
  };

  // E.2: resolve "Pods Blocked" by emitting a karpenter_update_pool
  // action whose patch widens the pool to admit currently-pending pods.
  // The patch is computed server-side from the unschedulable_pods list
  // (5 false-positive guards applied). Same approval rail.
  const handleResolveSchedulability = async (pool) => {
    if (!clusterId) return;
    setOptimizing((s) => ({ ...s, [`${pool.name}__sched`]: true }));
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(
          pool.name,
        )}/optimize-schedulability`,
      );
      setPools((cur) =>
        (cur || []).map((p) =>
          p.name === pool.name
            ? { ...p, status: 'pending_approval', pending_action_id: res.data?.action_id }
            : p,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setOptimizing((s) => ({ ...s, [`${pool.name}__sched`]: false }));
    }
  };

  // Phase C: modal success → optimistic flip the affected row (or, for
  // create, refetch on next 60s tick). Reusing the same pending_approval
  // surface as handleOptimize so operators see one consistent state.
  const handleModalSuccess = ({ mode, pool_name, action_id }) => {
    if (!pool_name) return;
    if (mode === 'create') {
      // No row to flip yet — the new pool will appear on the next inventory tick.
      return;
    }
    setPools((cur) =>
      (cur || []).map((p) =>
        p.name === pool_name
          ? { ...p, status: 'pending_approval', pending_action_id: action_id }
          : p,
      ),
    );
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings className="h-4 w-4 text-slate-400" />
          Karpenter Optimization
          <Badge className="bg-slate-100 text-slate-600 text-xs">
            {cloud === 'aws' ? 'AWS' : cloud === 'gcp' ? 'GCP' : '—'}
          </Badge>
          {totalSavings > 0 && (
            <Badge className="bg-emerald-100 text-emerald-800 text-xs">
              {fmtMo(totalSavings)} savings available
            </Badge>
          )}
          {staleness != null && staleness > 300 && (
            <Badge
              className="bg-amber-100 text-amber-800 text-xs inline-flex items-center gap-1"
              title="Data hasn't refreshed recently — check that the agent is running."
            >
              <AlertTriangle className="h-3 w-3" />
              Updated {Math.round(staleness / 60)}m ago
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setGitopsOpen(true)}
            title="Get Argo CD / Flux config so GitOps sync doesn't revert changes made by OptOps."
            className="inline-flex items-center gap-1"
          >
            <GitBranch className="h-3.5 w-3.5" /> GitOps setup
          </Button>
          <Button
            size="sm"
            variant="default"
            disabled={!cloud}
            onClick={() => setModal({ open: true, mode: 'create', pool: null })}
            className="inline-flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" /> Create NodePool
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      {!collapsed && (
        <CardContent className="space-y-3">
          {error && (
            <p className="text-xs text-red-600">Karpenter API error: {error}</p>
          )}

          {/* Filter chips */}
          <div className="flex flex-wrap gap-1">
            {Object.entries(FILTERS).map(([key, def]) => {
              const count =
                key === 'all'
                  ? (pools || []).length
                  : (pools || []).filter(def.match).length;
              return (
                <Badge
                  key={key}
                  className={
                    filter === key
                      ? 'bg-emerald-600 text-white cursor-pointer'
                      : 'bg-slate-100 text-slate-700 cursor-pointer hover:bg-slate-200'
                  }
                  onClick={() => setFilter(key)}
                >
                  {def.label} · {count}
                </Badge>
              );
            })}
          </div>

          {/* Pools table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b">
                <tr>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Name</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Lifecycle</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Instance Families</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Disruption</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium text-right">Weight</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium text-right">Nodes</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium text-right">Savings</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Status</th>
                  <th className="py-1.5 pr-3 text-[11px] uppercase tracking-wide text-slate-400 font-medium" />
                </tr>
              </thead>
              <tbody>
                {visiblePools && visiblePools.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-4 text-center text-slate-400 text-xs"
                    >
                      {pools && pools.length === 0
                        ? 'No node pools found. This view activates once Karpenter is running in the cluster.'
                        : 'No pools match the current filter.'}
                    </td>
                  </tr>
                )}
                {visiblePools &&
                  visiblePools.map((p) => (
                    <tr key={p.name} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <code className="font-mono text-xs bg-slate-50 px-1 rounded" title={p.name}>
                          {p.name}
                        </code>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {lifecycleBadge(p.lifecycle)}
                          {/* Sprint 8 #8 — spot diversification warning.
                              Renders only on pools admitting spot whose
                              admit-list has <3 instance types or <2 zones. */}
                          {p.recommended_diversification && (
                            <Badge
                              className="bg-amber-100 text-amber-800 text-[10px] inline-flex items-center gap-1"
                              title={`${p.recommended_diversification.instance_type_count} of ${p.recommended_diversification.min_types_required} recommended instance types`}
                            >
                              <AlertTriangle className="h-3 w-3" /> Low spot diversity
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {summarizeFamilies(p)}
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-600">
                        {summarizeDisruption(p)}
                      </td>
                      <td className="py-2 pr-3 text-right text-xs tabular-nums">
                        {p.weight || 0}
                      </td>
                      <td className="py-2 pr-3 text-right text-xs tabular-nums">
                        {p.node_count || 0}
                      </td>
                      <td className="py-2 pr-3 text-right text-xs font-medium tabular-nums">
                        {p.savings_instance_type_monthly > 0 ? (
                          <span className="text-emerald-700">
                            {fmtMo(p.savings_instance_type_monthly)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusPill
                          status={p.status}
                          savings={p.savings_instance_type_monthly}
                          wideningSavings={p.savings_with_widening_monthly}
                          blockedPodCount={(p.schedulability_recommendation?.blocked_pods || []).length}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1">
                          {/* Pending pool action: approve (apply) or dismiss
                              (pass) it right here — the retired backend
                              recommendations table no longer hosts approvals. */}
                          {p.status === 'pending_approval' && (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                disabled={
                                  !p.pending_action_id ||
                                  optimizing[`${p.name}__approve`] ||
                                  optimizing[`${p.name}__dismiss`]
                                }
                                onClick={() => handleApprove(p)}
                                title="Apply this pending change to the node pool"
                                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                {optimizing[`${p.name}__approve`] ? 'Approving…' : 'Approve'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={
                                  !p.pending_action_id ||
                                  optimizing[`${p.name}__approve`] ||
                                  optimizing[`${p.name}__dismiss`]
                                }
                                onClick={() => handleDismiss(p)}
                                title="Discard this pending change without applying it"
                                className="inline-flex items-center gap-1"
                              >
                                <X className="h-3 w-3" />
                                {optimizing[`${p.name}__dismiss`] ? 'Dismissing…' : 'Dismiss'}
                              </Button>
                            </>
                          )}
                          {/* E.2 — top priority when pending pods are blocked.
                              Shows up first in the row so operators don't
                              have to hunt for the unblock action. */}
                          {p.schedulability_recommendation && (
                            <Button
                              size="sm"
                              variant="default"
                              disabled={
                                p.status === 'pending_approval' ||
                                optimizing[`${p.name}__sched`]
                              }
                              onClick={() => handleResolveSchedulability(p)}
                              title={
                                p.schedulability_recommendation?.reason ||
                                'Widen pool to admit pending pods'
                              }
                              className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {optimizing[`${p.name}__sched`] ? 'Unblocking…' : 'Unblock pods'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={
                              p.status === 'savings_available' ? 'default' : 'outline'
                            }
                            disabled={
                              p.status === 'up_to_date' ||
                              p.status === 'widening_available' ||
                              p.status === 'schedulability_blocked' ||
                              p.status === 'pending_approval' ||
                              optimizing[p.name]
                            }
                            onClick={() => handleOptimize(p)}
                            title="Expand instance families to maximise savings"
                          >
                            {optimizing[p.name] ? 'Switching…' : 'Switch instance types'}
                          </Button>
                          {/* E.0 — widen pool when family/size restrictions
                              exclude cheaper instance types. Variant=default
                              when this is the headline action so operators
                              are drawn to it instead of the disabled Optimize. */}
                          {p.recommended_widening && (
                            <Button
                              size="sm"
                              variant={
                                p.status === 'widening_available' ? 'default' : 'outline'
                              }
                              disabled={
                                p.status === 'pending_approval' ||
                                optimizing[`${p.name}__widen`]
                              }
                              onClick={() => handleResolveWidening(p)}
                              title={
                                p.recommended_widening?.reason ||
                                'Widen pool constraints to admit cheaper instance types'
                              }
                              className="inline-flex items-center gap-1"
                            >
                              <Zap className="h-3 w-3" />
                              {optimizing[`${p.name}__widen`] ? 'Applying…' : 'Allow cheaper types'}
                            </Button>
                          )}
                          {/* Sprint 8 #1 — anti-fragmentation narrowing.
                              Surfaced only when the recommender flagged
                              ENI pod-density traps; otherwise the operator
                              never sees this button. */}
                          {p.recommended_narrowing &&
                            p.recommended_narrowing.remove_sizes?.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={
                                  p.status === 'pending_approval' ||
                                  optimizing[`${p.name}__narrow`]
                                }
                                onClick={() => handleResolveNarrowing(p)}
                                title={
                                  Object.entries(
                                    p.recommended_narrowing.reason_per_size || {},
                                  )
                                    .map(([s, r]) => `${s}: ${r}`)
                                    .join('\n') ||
                                  `Drop sizes: ${p.recommended_narrowing.remove_sizes.join(', ')}`
                                }
                                className="inline-flex items-center gap-1"
                              >
                                <Zap className="h-3 w-3 text-amber-600" />
                                {optimizing[`${p.name}__narrow`]
                                  ? 'Applying…'
                                  : 'Drop risky sizes'}
                              </Button>
                            )}
                          {/* Phase D.1 — disruption-policy optimization */}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              p.status === 'pending_approval' ||
                              optimizing[`${p.name}__disrupt`]
                            }
                            onClick={() => handleOptimizeDisruption(p)}
                            title="Tune consolidation cadence based on observed churn"
                            className="inline-flex items-center gap-1"
                          >
                            <Activity className="h-3 w-3" />
                            {optimizing[`${p.name}__disrupt`] ? 'Loading…' : 'Tune consolidation'}
                          </Button>
                          {/* Phase C — edit / delete */}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={p.status === 'pending_approval'}
                            onClick={() => setModal({ open: true, mode: 'edit', pool: p })}
                            title="Edit NodePool"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={p.status === 'pending_approval'}
                            onClick={() => setModal({ open: true, mode: 'delete', pool: p })}
                            title="Delete NodePool"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* NodeClass sub-table — Phase C.5 wires create/edit/delete */}
          <NodeClassTable
            nodeclasses={nodeclasses}
            cloud={cloud}
            onCreate={() => setNcModal({ open: true, mode: 'create', nodeclass: null })}
            onEdit={(nc) => setNcModal({ open: true, mode: 'edit', nodeclass: nc })}
            onDelete={(nc) => setNcModal({ open: true, mode: 'delete', nodeclass: nc })}
          />

          <div className="text-xs text-slate-500">
            Changes stay pending until you approve them.
          </div>
        </CardContent>
      )}

      {/* Phase C: Create / Edit / Delete pool modal */}
      <KarpenterPoolFormModal
        open={modal.open}
        mode={modal.mode}
        pool={modal.pool}
        clusterId={clusterId}
        nodeclasses={nodeclasses}
        cloud={cloud}
        onClose={() => setModal({ open: false, mode: null, pool: null })}
        onSuccess={handleModalSuccess}
      />

      {/* W7: GitOps steering snippets (Argo CD / Flux) */}
      <GitOpsSnippetsModal
        open={gitopsOpen}
        onClose={() => setGitopsOpen(false)}
        clusterId={clusterId}
      />

      {/* Phase C.5: Create / Edit / Delete NodeClass modal */}
      <KarpenterNodeClassFormModal
        open={ncModal.open}
        mode={ncModal.mode}
        cloud={cloud}
        clusterId={clusterId}
        nodeclass={ncModal.nodeclass}
        onClose={() => setNcModal({ open: false, mode: null, nodeclass: null })}
        onSuccess={() => setNcModal({ open: false, mode: null, nodeclass: null })}
      />

      {/* Auto-optimize disruption preview dialog. Shows the server-side
          recommendation (churn-history + utilization-driven) with
          reasoning so the operator sees WHY before applying. */}
      <Dialog
        open={Boolean(disruptionPreview)}
        onOpenChange={(v) => { if (!v) setDisruptionPreview(null); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Tune consolidation — {disruptionPreview?.pool?.name}
            </DialogTitle>
            <DialogDescription>
              Based on this pool&rsquo;s utilization and node churn over the
              last 14 days. Nothing changes until you approve.
            </DialogDescription>
          </DialogHeader>

          {disruptionPreview && (
            <div className="space-y-3 py-2">
              {/* Current state */}
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Current</div>
                <div className="text-sm space-y-0.5">
                  <div>
                    Consolidate after:{' '}
                    <span className="font-mono text-xs">{disruptionPreview.current?.consolidate_after || '—'}</span>
                  </div>
                  <div>
                    Policy:{' '}
                    <span className="font-mono text-xs">{disruptionPreview.current?.consolidation_policy || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Recommendation (or "no change" reasoning) */}
              <div className={`rounded-md border p-3 ${
                disruptionPreview.recommendation
                  ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10'
                  : 'border-slate-200 bg-slate-50 dark:bg-surface-dark'
              }`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className={`text-xs font-semibold uppercase tracking-wide ${
                    disruptionPreview.recommendation
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    {disruptionPreview.recommendation ? 'Recommended' : 'No change suggested'}
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    Confidence: {disruptionPreview.confidence}
                  </Badge>
                </div>
                {disruptionPreview.recommendation && (
                  <div
                    className="text-sm mb-2"
                    title={`Budgets: ${JSON.stringify(disruptionPreview.recommendation.budgets)}`}
                  >
                    Consolidate after:{' '}
                    <span className="font-mono text-xs">
                      {disruptionPreview.current?.consolidate_after || '—'} → {disruptionPreview.recommendation.consolidate_after}
                    </span>
                  </div>
                )}
                <p className="text-sm text-slate-700 dark:text-slate-200">{disruptionPreview.reasoning}</p>
              </div>

              {/* Signals */}
              {disruptionPreview.signals && (
                <div className="text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-3">
                  <span>CPU 24h avg: {disruptionPreview.signals.cpu_pct_avg_24h?.toFixed(1) ?? '—'}%</span>
                  <span>MEM 24h avg: {disruptionPreview.signals.mem_pct_avg_24h?.toFixed(1) ?? '—'}%</span>
                  <span>Pool churn 14d: {disruptionPreview.history_count ?? 0}</span>
                  <span>Metric samples: {disruptionPreview.signals.sample_count ?? 0}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisruptionPreview(null)}>Cancel</Button>
            <Button
              onClick={confirmOptimizeDisruption}
              disabled={
                !disruptionPreview?.recommendation ||
                Boolean(optimizing[`${disruptionPreview?.pool?.name}__disrupt`])
              }
            >
              {optimizing[`${disruptionPreview?.pool?.name}__disrupt`] ? 'Applying…' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default KarpenterPanel;
